import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Envelope, Phone, MapPin, PaperPlaneTilt, 
  Check, ArrowRight, ChatCircle
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';

const Contact = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    toast.success(t('contact_success_title'));
  };

  const subjects = [
    { value: 'general', label: t('contact_subject_general') },
    { value: 'support', label: t('contact_subject_support') },
    { value: 'merchant', label: t('contact_subject_merchant') },
    { value: 'press', label: t('contact_subject_press') },
    { value: 'other', label: t('contact_subject_other') }
  ];

  const contactInfo = [
    {
      icon: Envelope,
      title: t('contact_email'),
      value: 'hello@habitue.com',
      link: 'mailto:hello@habitue.com'
    },
    {
      icon: Phone,
      title: t('contact_phone'),
      value: '+33 1 23 45 67 89',
      link: 'tel:+33123456789'
    },
    {
      icon: MapPin,
      title: t('contact_address'),
      value: '123 Rue de la Fidélité, 75010 Paris',
      link: null
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0F1A14] text-white">
        <GlobalNav />
        <div className="pt-32 pb-20 px-6 flex items-center justify-center min-h-screen">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-md"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-20 h-20 rounded-full bg-[#145A3A] flex items-center justify-center mx-auto mb-6"
            >
              <Check size={40} className="text-[#C3A365]" weight="bold" />
            </motion.div>
            <h1 className="text-3xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('contact_success_title')}
            </h1>
            <p className="text-[#8BA89A] mb-8">
              {t('contact_success_desc')}
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#C3A365] text-[#0F1A14] font-semibold hover:bg-[#D4B576] transition-colors"
              data-testid="contact-success-btn"
            >
              {t('contact_success_btn')} <ArrowRight size={18} />
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#1E2F26] border border-[#283D32] px-4 py-2 rounded-full mb-8"
          >
            <ChatCircle size={16} className="text-[#C3A365]" />
            <span className="text-sm text-[#8BA89A]">{t('contact_badge')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('contact_title')}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-xl mx-auto"
          >
            {t('contact_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <h2 className="text-2xl font-light tracking-tight mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('contact_info_title')}
              </h2>

              <div className="space-y-6 mb-12">
                {contactInfo.map((info, i) => {
                  const Icon = info.icon;
                  return (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#1E2F26] flex items-center justify-center flex-shrink-0">
                        <Icon size={22} className="text-[#C3A365]" />
                      </div>
                      <div>
                        <p className="text-[#8BA89A] text-sm mb-1">{info.title}</p>
                        {info.link ? (
                          <a href={info.link} className="text-white hover:text-[#C3A365] transition-colors">
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-white">{info.value}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-6 bg-[#1E2F26] border border-[#283D32]">
                <h3 className="text-white font-medium mb-2">{t('contact_hours_title')}</h3>
                <p className="text-[#8BA89A] text-sm whitespace-pre-line">
                  {t('contact_hours')}
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className="bg-[#18261E] border border-[#283D32] p-8">
                <h2 className="text-2xl font-light tracking-tight mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t('contact_form_title')}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[#8BA89A] text-sm mb-2">{t('contact_name')}</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-[#1E2F26] border border-[#283D32] rounded-lg px-4 py-3 text-white placeholder:text-[#8BA89A]/50 focus:outline-none focus:border-[#C3A365]/50 transition-colors"
                        placeholder={t('contact_name_placeholder')}
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-[#8BA89A] text-sm mb-2">{t('contact_email')}</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-[#1E2F26] border border-[#283D32] rounded-lg px-4 py-3 text-white placeholder:text-[#8BA89A]/50 focus:outline-none focus:border-[#C3A365]/50 transition-colors"
                        placeholder={t('contact_email_placeholder')}
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#8BA89A] text-sm mb-2">{t('contact_subject')}</label>
                    <select
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full bg-[#1E2F26] border border-[#283D32] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C3A365]/50 transition-colors"
                      data-testid="contact-subject-select"
                    >
                      {subjects.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#8BA89A] text-sm mb-2">{t('contact_message')}</label>
                    <textarea
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-[#1E2F26] border border-[#283D32] rounded-lg px-4 py-3 text-white placeholder:text-[#8BA89A]/50 focus:outline-none focus:border-[#C3A365]/50 transition-colors resize-none"
                      placeholder={t('contact_message_placeholder')}
                      data-testid="contact-message-input"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    className="w-full py-4 bg-[#C3A365] text-[#0F1A14] font-semibold flex items-center justify-center gap-2 hover:bg-[#D4B576] transition-colors"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    data-testid="contact-submit-btn"
                  >
                    <PaperPlaneTilt size={20} />
                    {t('contact_send')}
                  </motion.button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
